import React, { useRef, useEffect, useState } from 'react'
import * as d3 from 'd3'

import * as settings from '../settings.js'
import './styles.scss'

const getRepresentingAgeArray = (age) => {
  return Array.apply(0, Array(settings.kings.representingMargin * 2 + 1)).map(
    (element, index) =>
      index + age - settings.kings.representingMargin
  )
}

const listItemInListItem = (a: Array<number>, b: Array<number>) => {
  for(let x in b)
  {
    if(a.includes(b[x]))
      return true
  }
  return false
}

export const AgeGraph = ({ 
  data,
  userData,
  highlightedKings,
  highlightedKingsChanged
  }: {
  data: object,
  userData: object,
  highlightedKings: Array<number>,
  highlightedKingsChanged: any
}) => {
  // const myRef = React.createRef();
  const myRef = useRef()

  useEffect(() => {
    const svg = d3.select(myRef.current)

    // Create g Räte
    svg.append('g').attr('class', 'kings')

    // Add xAxis
    svg.append('g').attr('class', 'xAxis')

    // Add yAxis
    svg.append('g').attr('class', 'yAxis')

    // Create g Bars
    svg.append('g').attr('class', 'bars')    

    // Add g User
    svg.append('g').attr('class', 'user')
  }, [])

  // Create X-Axis
  const x = d3.scaleLinear()

  const xAxis = g =>
    g
      .attr(
        'transform',
        `translate(0,${settings.size.height - settings.margin.bottom})`
      )
      .call(
        d3
          .axisBottom(x)
          .ticks(settings.size.width / 80)
          .tickSizeOuter(0)
      )
      .call(g => g.select('.xtext').remove())
      .call(g =>
        g
          .append("g")
          .attr("class", "tick")
            .append('text')
              .attr("class", 'xtext')
              .attr('x', settings.size.width - settings.margin.right)
              .attr('y', -4)
              .attr('fill', 'currentColor')
              .attr('font-weight', 'bold')
              .attr('text-anchor', 'end')
              .text('Alter')
      )

  // Create Y-Axis
  const y = d3.scaleLinear()

  const yAxis = g =>
    g
      .attr('transform', `translate(${settings.margin.left},0)`)
      .call(
        d3.axisLeft(y)
          .ticks(settings.size.height / 40)
          .tickSize(-x(100))
      )
      .call(g => g.select('.domain').remove())
      .call(g => g.select('.ytext').remove())
      .call(g =>
        g
          .select('.tick:last-of-type text')
          .clone()
          .attr('x', 4)
          .attr("class", 'ytext')
          .attr('text-anchor', 'start')
          .attr('font-weight', 'bold')
          .text('Personen')
      )

  // Data Change Effect
  useEffect(() => {
    const svg = d3.select(myRef.current)

    const ages = data.agedistribution

    // Move UserAge-Entry to last position of array (problem with stroke)
    const ageIndex = ages.findIndex(e => e.age === userData.age)
    if (ageIndex >= 0) {
      ages.push(ages.splice(ageIndex, 1)[0])
    }

    // Update x and y domains
    x.domain([d3.min(ages, d => d.age), d3.max(ages, d => d.age)]).range([
      settings.margin.left,
      settings.size.width - settings.margin.right
    ])

    y.domain([0, d3.max(ages, d => d.count)])
      .nice()
      .range([
        settings.size.height - settings.margin.bottom,
        settings.margin.top
      ])

    // Update bars
    svg
      .selectAll('.bars')
      .selectAll('rect')
      .data(ages)
      .join('rect')
      .attr('x', d => x(d.age) - settings.bar.size / 2)
      .attr('width', settings.bar.size)
      .attr('y', d => y(d.count))
      .attr('height', d => y(0) - y(d.count))
      .attr('fill', d => {
        for(let i in highlightedKings)
        {
          let king = data.executives.find(e => e.id === highlightedKings[i])

          if(getRepresentingAgeArray(king.agecalculated).includes(d.age))
          {
            return settings.colors.highlight
          }
        }
        // if (d.represented) return settings.colors.represented
        // else return settings.colors.notRepresented
        if(d.yeartorepresentative <= 5) return settings.colors.representedRamp[0]
        else if(d.yeartorepresentative <= 6) return settings.colors.representedRamp[1]
        else if(d.yeartorepresentative <= 7) return settings.colors.representedRamp[2]
        else if(d.yeartorepresentative <= 8) return settings.colors.representedRamp[3]
        else if(d.yeartorepresentative <= 9) return settings.colors.representedRamp[4]
        else if(d.yeartorepresentative <= 10) return settings.colors.representedRamp[5]
        else return settings.colors.representedRamp[settings.colors.representedRamp[5].length - 1]
      })
      .attr('stroke', d =>
        d.age !== userData.age ? 'none' : settings.colors.userAge
      )
      .attr('stroke-width', d => (d.age === userData.age ? 1.5 : 0))
      .on('mouseover', function(d, i) {

        // Select "nearest" King or Queen
        const m = settings.kings.representingMargin
        const reprKings = data.representativeages.filter(
          p => p.age >= i.age - m && p.age <= i.age + m
        )

        let nearestKing = null
        let nearestAge: number|null = null
        let nearestGap: number = 99
        for (const k in reprKings) {
          if (Math.abs(reprKings[k].age - i.age) < nearestGap) {
            nearestGap = Math.abs(reprKings[k].age - i.age)
            nearestKing = reprKings[k].ids
            nearestAge = reprKings[k].age
          }
        }

        // Select possible Groups and colorize them
        if (nearestKing) {
          highlightedKingsChanged(nearestKing)
        }
      })
      .on('mouseleave', () => {
        highlightedKingsChanged([])
      })

    // Update x-axis
    svg.selectAll('.xAxis').call(xAxis)

    // Update y-axis
    svg.selectAll('.yAxis').call(yAxis)

    // Add Gemeinderaete
    const kings = data.representativeages

      // Add Line
    const kingDom = svg
      .select('.kings')
      .selectAll('g')
      .data(kings)

    kingDom.exit().remove()

    const kg = kingDom
      .enter()
      .append('g')
      .merge(kingDom)
      .attr('data-age', d => d.alter)

    kg.selectAll('line').remove()
    kg.append('line')
      .attr('class', 'kingline')
      .attr('x1', d => x(d.age))
      .attr('x2', d => x(d.age))
      .attr('y1', y(0))
      .attr('y2', settings.kings.top)
      .attr('stroke', d => listItemInListItem(highlightedKings, d.ids) ? settings.kings.circleColorHover : settings.kings.lineColor)

    kg.selectAll('circle').remove()
    kg.append('circle')
      .attr('class', 'kingcircle')
      .attr('cx', d => x(d.age))
      .attr('cy', settings.kings.top)
      .attr('r', settings.kings.r)
      .attr('fill', d => listItemInListItem(highlightedKings, d.ids) ? settings.kings.circleColorHover : settings.kings.circleColor)
      .attr("stroke", 'white')
      .attr("stroke-width", 0.5)

      .on('mouseover', (e, d) => {

        // Set which age to colorize
        highlightedKingsChanged(d.ids)
  
      })
      .on('mouseout', () => {
        highlightedKingsChanged([])
      })

    // Add User Arrow

    if(userData.age != null && !Number.isNaN(userData.age))
    {
      var direction:number
      if(userData.age <= 50)
        direction = userData.age < 20 ? -1 : 1
      else
        direction = userData.age < 80 ? -1 : 1

      const yValue: number = y(ages.find(e => e.age === userData.age).count) - 40
      const xValue: number = x(userData.age) - 35 * direction

      const xLabel: number = xValue - 6 * direction;
      const yLabel: number = yValue + 6

      svg
        .selectAll('.user')
        .selectAll('path')
        .data([userData.age])
        .join('path')
          .attr("class", 'userarrow')
          .attr("transform", `translate(${xValue} ${yValue}) scale(2) scale(${direction}, 1)`)
          .attr('d', `M14.28 13.97l1.08-1 .94 1a11.87 11.87 0 00-3.52-8.3C9.31 2.35 4.17 2 2.12 2a9.13 9.13 0 00-.95 0h-.5L.54.58H2.1c2.24 0 7.85.39 11.7 4a13.33 13.33 0 014 9.33l.87-1 1.11 1-2.69 3z`)
          .attr('fill', '#000')
          .attr('stroke', 'white')

      // Add User Text
      svg
        .selectAll('.user')
        .selectAll('text')
        .data([userData.age])
        .join('text')
        .text('Ihr Alter')
        .attr('class', 'userage')
        .attr('text-anchor', direction > 0 ? 'end' : 'begin')
        .attr('x', xLabel)
        .attr('y', yLabel)
        .attr('fill', 'grey')
    }
  }, [data, userData, highlightedKings])

  return (
    <div className='agegraph'>

      <svg
        ref={myRef}
        className='agegraph'
        viewBox={`0, 0, ${settings.size.width}, ${settings.size.height}`}
      >
      </svg>
    </div>
  )
}
